// Copyright (c) 2023-present VexFlow contributors: https://github.com/vexflow/vexflow/graphs/contributors
//
// This class implements dot modifiers for notes.
import { Glyphs } from './glyphs';
import { Modifier } from './modifier';
import { Category, isStaveNote, isTabNote } from './typeguard';
import { RuntimeError } from './util';
export class Dot extends Modifier {
    static get CATEGORY() {
        return Category.Dot;
    }
    /** Returns the dots associated to a Note. */
    static getDots(note) {
        return note.getModifiersByType(Dot.CATEGORY);
    }
    /** Add a dot on the specified keys to the notes. */
    static buildAndAttach(notes, options) {
        for (const note of notes) {
            if (options === null || options === void 0 ? void 0 : options.all) {
                for (let i = 0; i < note.keys.length; i++) {
                    const dot = new Dot();
                    note.addModifier(dot, i);
                }
            }
            else if ((options === null || options === void 0 ? void 0 : options.index) !== undefined) {
                const dot = new Dot();
                note.addModifier(dot, options === null || options === void 0 ? void 0 : options.index);
            }
            else {
                const dot = new Dot();
                note.addModifier(dot, 0);
            }
        }
    }
    // Arrange dots inside a ModifierContext.
    static format(dots, state) {
        const rightShift = state.rightShift;
        const dotSpacing = 1;
        if (!dots || dots.length === 0)
            return false;
        const dotList = [];
        const maxShiftMap = {};
        for (let i = 0; i < dots.length; ++i) {
            const dot = dots[i];
            const note = dot.getNote();
            let props;
            let shift;
            if (isStaveNote(note)) {
                const index = dot.checkIndex();
                props = note.getKeyProps()[index];
                // consider right displaced head with no previous modifier
                shift = note.getFirstDotPx();
            }
            else if (isTabNote(note)) {
                props = { line: 0.5 }; // Shim key props for dot placement
                shift = rightShift;
            }
            else {
                // note object is not StaveNote or TabNote.
                throw new RuntimeError('Internal', 'Unexpected instance.');
            }
            const noteId = note.getAttribute('id');
            dotList.push({ line: props.line, note, noteId, dot });
            maxShiftMap[noteId] = Math.max(maxShiftMap[noteId] || shift, shift);
        }
        // Sort dots by line number.
        dotList.sort((a, b) => b.line - a.line);
        let dotShift = rightShift;
        let xWidth = 0;
        let lastLine = null;
        let lastNote = null;
        let prevDottedSpace = null;
        let halfShiftY = 0;
        for (let i = 0; i < dotList.length; ++i) {
            const { dot, note, noteId, line } = dotList[i];
            // Reset the position of the dot every line.
            if (line !== lastLine || note !== lastNote) {
                dotShift = maxShiftMap[noteId];
            }
            if (!note.isRest() && line !== lastLine) {
                if (Math.abs(line % 1) === 0.5) {
                    // note is on a space, so no dot shift
                    halfShiftY = 0;
                }
                else {
                    // note is on a line, so shift dot to space above the line
                    halfShiftY = 0.5;
                    if (lastNote !== null && !lastNote.isRest() && lastLine !== null && lastLine - line === 0.5) {
                        // previous note on a space, so shift dot to space below the line
                        halfShiftY = -0.5;
                    }
                    else if (line + halfShiftY === prevDottedSpace) {
                        // previous space is dotted, so shift dot to space below the line
                        halfShiftY = -0.5;
                    }
                }
            }
            // Convert halfShiftY to a multiplier for dots.draw().
            if (note.isRest()) {
                dot.dotShiftY += -halfShiftY;
            }
            else {
                dot.dotShiftY = -halfShiftY;
            }
            prevDottedSpace = line + halfShiftY;
            dot.setXShift(dotShift);
            dotShift += dot.getWidth() + dotSpacing; // spacing
            xWidth = dotShift > xWidth ? dotShift : xWidth;
            lastLine = line;
            lastNote = note;
        }
        // Update state.
        state.rightShift += xWidth;
        return true;
    }
    constructor() {
        super();
        this.position = Modifier.Position.RIGHT;
        this.setText(Glyphs.augmentationDot);
        this.dotShiftY = 0;
    }
    setNote(note) {
        this.note = note;
        this.font = note.font;
        return this;
    }
    setDotShiftY(y) {
        this.dotShiftY = y;
        return this;
    }
    draw() {
        const ctx = this.checkContext();
        const note = this.checkAttachedNote();
        this.setRendered();
        const stave = note.checkStave();
        const lineSpace = stave.getSpacingBetweenLines();
        const start = note.getModifierStartXY(this.position, this.index, { forceFlagRight: true });
        // Set the starting y coordinate to the base of the stem for TabNotes.
        if (isTabNote(note)) {
            start.y = note.getStemExtents().baseY;
        }
        this.x = start.x;
        this.y = start.y + this.dotShiftY * lineSpace;
        this.renderText(ctx, 0, 0);
    }
}
